import type { Field } from '@atlas/react-forms';
import classnames from 'classnames';
import {
  type FocusEvent,
  type InputHTMLAttributes,
  forwardRef,
  useState,
} from 'react';

import Icon from 'components/Icon/Icon';
import Tooltip from 'components/Tooltip';
import { Translation, useTranslation } from 'utils/translations';

import styles from './Select.module.scss';

type SelectProps = InputHTMLAttributes<HTMLSelectElement> & {
  isValid?: boolean;
  placeholder?: string;
  translateOptions?: boolean;
  options: Field['options'];
};

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      value,
      onChange,
      onFocus,
      onBlur,
      className,
      name,
      disabled,
      id,
      isValid,
      placeholder,
      options,
      translateOptions,
      ...restProps
    },
    ref,
  ) => {
    const defaultLabel = useTranslation('Choose an option');
    const [showError, setShowError] = useState(false);
    const lockedHintText = useTranslation('locked_field_hint');

    const label = options.find((option) => option.value === value)?.text;
    const labelText =
      (label && translateOptions ? <Translation message={label} /> : label) ||
      placeholder ||
      defaultLabel;

    const handleFocusEvent = (
      event: FocusEvent<HTMLSelectElement, Element>,
    ) => {
      onFocus?.(event);
      setShowError(false);
    };

    const handleBlurEvent = (event: FocusEvent<HTMLSelectElement, Element>) => {
      onBlur?.(event);
      setShowError(true);
    };

    return (
      <div
        className={classnames(styles.select, className, {
          [styles.error]: !isValid && showError,
          [styles.disabled]: disabled,
        })}
      >
        <div className={styles.control}>
          {disabled ? (
            <Tooltip message={lockedHintText}>
              <Icon className={styles['select-icon']} name="lock" />
            </Tooltip>
          ) : (
            <Icon className={styles['select-icon']} name="arrow-down" />
          )}
        </div>
        <span className={styles.label}>{labelText}</span>
        <select
          id={id}
          name={name}
          disabled={disabled}
          onChange={onChange}
          onFocus={handleFocusEvent}
          onBlur={handleBlurEvent}
          value={value}
          ref={ref}
          {...restProps}
        >
          <option value="" disabled>
            {placeholder || defaultLabel}
          </option>
          {options.map((option) => (
            <option key={option.text} value={option.value || ''}>
              {translateOptions ? (
                <Translation message={option.text} />
              ) : (
                option.text
              )}
            </option>
          ))}
        </select>
      </div>
    );
  },
);

export default Select;
